import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Download, Loader2, Wallet } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, Field, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { listItems } from '@/lib/items-api';
import { nazwaOperacji } from '@/lib/kody-operacji';
import { useOpoznione } from '@/lib/opoznienie';
import { pobierzRaport } from '@/lib/raporty-api';
import { podgladKorektyCeny, wykonajKorekteCeny, type PodgladKorekty } from '@/lib/transactions-api';
import { cn } from '@/lib/utils';

/**
 * KOREKTA CENY W HISTORII WYDAŃ — okno SAMODZIELNE, otwierane przyciskiem nad listą.
 *
 * ⚠️ PIERWSZA WERSJA WCHODZIŁA IKONKĄ PRZY WIERSZU I TO BYŁO ŹLE ZAPROJEKTOWANE — zgłoszone
 * wprost: „trzeba jakoś szukać na liście transakcji". Żeby poprawić cenę rękawic za lipiec,
 * trzeba było najpierw ZNALEŹĆ na liście jakiekolwiek ich wydanie. Teraz towar i zakres dat
 * wybiera się tutaj, a lista transakcji nie musi być w ogóle ustawiona.
 *
 * ⚠️ OPERACJA ZMIENIA PIENIĄDZE W ZAMKNIĘTYCH OKRESACH i po stronie SPS nie ma cofnięcia. Dlatego
 * okno jest dwustopniowe: najpierw podgląd (ile wierszy, jakie ceny były, wartość przed i po),
 * a przycisk zatwierdzenia pojawia się dopiero po nim.
 */

/** Cztery kody, które klienci realnie używają — zmierzone na bazie, nie wybrane z gustu. */
const PODSTAWOWE = ['WN', 'REN', 'IA', 'DD'] as const;

const zl = (n: number): string => n.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });

/**
 * WYBÓR TOWARU — wpisywanie JEST filtrem listy, nie osobnym polem obok niej.
 *
 * ⚠️ TEN SAM UKŁAD CO PRZY PRACOWNIKU W RAPORTACH i z tego samego powodu: dwa pola (szukajka
 * plus lista) wymagają zrozumienia, że jedno zawęża drugie, a wyglądają jak dwa niezależne filtry.
 * Katalog u klienta ma kilkaset pozycji, więc natywna lista bez filtrowania jest nieużywalna.
 */
function PoleTowaru({
  groupId,
  wybrany,
  onWybierz,
}: {
  groupId: string;
  wybrany: { id: string; name: string; itemNumber: string } | null;
  onWybierz: (t: { id: string; name: string; itemNumber: string } | null) => void;
}) {
  const { t } = useI18n();
  const [wpisane, setWpisane] = useState('');
  const [otwarte, setOtwarte] = useState(false);
  const fraza = useOpoznione(wpisane, 250);

  const lista = useQuery({
    queryKey: ['items', 'do-korekty', groupId, fraza],
    queryFn: () =>
      listItems({
        groupId,
        page: 1,
        pageSize: 30,
        search: fraza,
        sort: 'name',
        dir: 'asc',
        /** ⚠️ `all`: korekta dotyczy historii, więc towar WYCOFANY też musi dać się wybrać. */
        status: 'all',
        duplicateSupplierPart: false,
      }),
    enabled: otwarte || fraza !== '',
  });

  return (
    <div className="relative">
      <Input
        value={wpisane}
        placeholder={wybrany === null ? t('korekta.szukajTowaru') : `${wybrany.name} · ${wybrany.itemNumber}`}
        onChange={(e) => {
          setWpisane(e.target.value);
          setOtwarte(true);
          /** ⚠️ Pisanie unieważnia wybór — inaczej korekta dotyczyłaby towaru, którego nie widać. */
          if (wybrany !== null) onWybierz(null);
        }}
        onFocus={() => setOtwarte(true)}
        onBlur={() => setOtwarte(false)}
      />
      {otwarte && (
        <div className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-[var(--popover,var(--card))] shadow-md">
          {lista.isPending && <p className="px-2.5 py-1.5 text-xs text-muted-foreground">{t('common.loading')}</p>}
          {(lista.data?.rows ?? []).map((it) => (
            /* `onMouseDown` z `preventDefault`, bo `onBlur` zamknąłby listę przed kliknięciem. */
            <button
              key={it.id}
              type="button"
              className="block w-full px-2.5 py-1.5 text-left text-xs hover:bg-accent"
              onMouseDown={(e) => {
                e.preventDefault();
                onWybierz({ id: it.id, name: it.name, itemNumber: it.itemNumber });
                setWpisane('');
                setOtwarte(false);
              }}
            >
              {it.name} <span className="text-muted-foreground">· {it.itemNumber}</span>
            </button>
          ))}
          {!lista.isPending && (lista.data?.rows ?? []).length === 0 && (
            <p className="px-2.5 py-1.5 text-xs text-muted-foreground">{t('korekta.brakTowarow')}</p>
          )}
        </div>
      )}
    </div>
  );
}

export function KorektaCeny({
  groupId,
  machineId,
  domyslneOd,
  domyslneDo,
  kodyWDanych,
  onClose,
}: {
  groupId: string;
  /** Zawężenie do automatu z filtra ekranu; korekta bez niego dotyczy całej wyspy. */
  machineId?: string;
  /** Podpowiedź z filtrów listy — do NADPISANIA, nie do przyjęcia w ciemno. */
  domyslneOd: string;
  domyslneDo: string;
  /** Kody operacji WYSTĘPUJĄCE w danych tej wyspy — patrz lista „dodatkowych". */
  kodyWDanych: string[];
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();

  const [towar, setTowar] = useState<{ id: string; name: string; itemNumber: string } | null>(null);
  const [od, setOd] = useState(domyslneOd.slice(0, 10));
  const [doDnia, setDoDnia] = useState(domyslneDo.slice(0, 10));
  const [cena, setCena] = useState('');
  const [wszystkieTypy, setWszystkieTypy] = useState(true);
  const [kody, setKody] = useState<string[]>([...PODSTAWOWE]);
  const [zmienBiezaca, setZmienBiezaca] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);
  const [podglad, setPodglad] = useState<PodgladKorekty | null>(null);

  /** Godziny dopisujemy tu, a nie w polach: korekta dotyczy CAŁYCH dni, bo tak brzmi zamówienie. */
  const zakres = () => ({ od: `${od} 00:00`, do: `${doDnia} 23:59` });

  const zadanie = () => ({
    groupId,
    itemId: towar?.id ?? '',
    ...(machineId ? { machineId } : {}),
    ...zakres(),
    cena: Number(cena.replace(',', '.')),
    ...(wszystkieTypy ? {} : { kody }),
    zmienCeneBiezaca: zmienBiezaca,
  });

  /**
   * ⚠️ KAŻDA ZMIANA PARAMETRÓW UNIEWAŻNIA PODGLĄD. Bez tego dałoby się obejrzeć liczby dla jednego
   * zakresu, zmienić datę i zatwierdzić — czyli zatwierdzić coś, czego nikt nie widział.
   */
  const zmien =
    <T,>(ustaw: (v: T) => void) =>
    (v: T) => {
      setPodglad(null);
      setBlad(null);
      ustaw(v);
    };

  const pobierzPodglad = useMutation({
    mutationFn: () => podgladKorektyCeny(zadanie()),
    onSuccess: setPodglad,
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  const zatwierdz = useMutation({
    mutationFn: () => wykonajKorekteCeny(zadanie()),
    onSuccess: (r) => {
      toast.success(t('korekta.zrobione').replace('{n}', String(r.poprawionych)));
      onClose();
    },
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  /**
   * EKSPORT WIERSZY OBJĘTYCH KOREKTĄ — te same filtry, ta sama droga co raport z ekranu.
   *
   * ⚠️ SKŁADA GO SERWER (`/reports/plik/wydania`), a nie przeglądarka, więc plik jest dokładnie
   * tym samym, co raport wydań i co wysyłka z harmonogramu. Filtr `kody` dodany po to, żeby plik
   * pokazywał DOKŁADNIE te wiersze, które zmiana obejmie — nie mniej i nie więcej.
   */
  const eksport = useMutation({
    mutationFn: () =>
      pobierzRaport('transactions', {
        groupId,
        itemId: towar?.id,
        ...(machineId ? { machineId } : {}),
        ...zakres(),
        ...(wszystkieTypy ? {} : { kody }),
        sort: 'ts',
        dir: 'asc',
        format: 'xlsx',
      }),
    onSuccess: (r) => toast.success(t('korekta.eksportGotowy').replace('{n}', String(r.wierszy))),
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  const cenaOk = cena.trim() !== '' && Number.isFinite(Number(cena.replace(',', '.')));
  const daneOk = towar !== null && od !== '' && doDnia !== '' && od <= doDnia;
  const dodatkowe = kodyWDanych.filter((k) => !(PODSTAWOWE as readonly string[]).includes(k));

  const przelacz = (kod: string): void =>
    zmien(setKody)(kody.includes(kod) ? kody.filter((k) => k !== kod) : [...kody, kod]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent title={t('korekta.tytul')} description={t('korekta.opis')} className="w-[min(40rem,calc(100vw-2rem))]">
        <FormGrid>
          <Field label={t('korekta.towar')} htmlFor="towar" required>
            <PoleTowaru groupId={groupId} wybrany={towar} onWybierz={zmien(setTowar)} />
            {towar !== null && (
              <p className="mt-1 text-xs text-muted-foreground">
                {towar.name} · <span className="font-mono">{towar.itemNumber}</span>
              </p>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('korekta.od')} htmlFor="od" required>
              <Input id="od" type="date" value={od} onChange={(e) => zmien(setOd)(e.target.value)} />
            </Field>
            <Field label={t('korekta.do')} htmlFor="do" required>
              <Input id="do" type="date" value={doDnia} onChange={(e) => zmien(setDoDnia)(e.target.value)} />
            </Field>
          </div>

          <Field label={t('korekta.cena')} htmlFor="cena" required>
            <Input
              id="cena"
              inputMode="decimal"
              placeholder="10,00"
              value={cena}
              onChange={(e) => zmien(setCena)(e.target.value)}
            />
          </Field>

          {/*
            ⚠️ UKŁAD ZAMÓWIONY WPROST: ptaszek „wszystkie typy operacji" zaznaczony domyślnie,
            a lista wyboru pojawia się DOPIERO po jego odznaczeniu — z czterema podstawowymi u góry
            i dodatkowymi pod spodem.
          */}
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={wszystkieTypy} onChange={(e) => zmien(setWszystkieTypy)(e.target.checked)} />
              {t('korekta.wszystkieTypy')}
            </label>

            {!wszystkieTypy && (
              <div className="mt-2 space-y-2 rounded-md border border-border p-2">
                <div>
                  <p className="mb-1 text-xs font-medium">{t('korekta.podstawowe')}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {PODSTAWOWE.map((kod) => (
                      <label key={kod} className="flex items-center gap-1.5 text-xs">
                        <input type="checkbox" checked={kody.includes(kod)} onChange={() => przelacz(kod)} />
                        <span className="font-mono">{kod.toLowerCase()}</span>
                        <span className="text-muted-foreground">{nazwaOperacji(t, kod)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {dodatkowe.length > 0 && (
                  <div className="border-t border-border/60 pt-2">
                    {/*
                      ⚠️ „Dodatkowe" to kody WYSTĘPUJĄCE W DANYCH tej wyspy, a nie cały słownik 49
                      pozycji: lista wszystkich znaczyłaby zaznaczanie kodów, których u tego klienta
                      nie ma ani razu — i człowiek dowiadywałby się tego dopiero z pustego podglądu.
                    */}
                    <p className="mb-1 text-xs font-medium">{t('korekta.dodatkowe')}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {dodatkowe.map((kod) => (
                        <label key={kod} className="flex items-center gap-1.5 text-xs">
                          <input type="checkbox" checked={kody.includes(kod)} onChange={() => przelacz(kod)} />
                          <span className="font-mono">{kod.toLowerCase()}</span>
                          <span className="text-muted-foreground">{nazwaOperacji(t, kod)}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={zmienBiezaca}
              onChange={(e) => zmien(setZmienBiezaca)(e.target.checked)}
            />
            <span>
              {t('korekta.biezaca')}
              <span className="block text-xs text-muted-foreground">{t('korekta.biezacaHint')}</span>
            </span>
          </label>

          {podglad !== null && (
            <div className="rounded-md border border-border p-3 text-xs">
              {podglad.doZmiany === 0 ? (
                <p>{t('korekta.nicDoZmiany')}</p>
              ) : (
                <>
                  <p className="text-sm font-medium">{t('korekta.doZmiany').replace('{n}', String(podglad.doZmiany))}</p>
                  <p className="mt-1">
                    {t('korekta.byloCeny')} {podglad.ceny.map((c) => `${zl(c.cena)} × ${c.wierszy}`).join(', ')}
                  </p>
                  <p className="mt-1">
                    {t('korekta.wartosc')} <span className="font-medium">{zl(podglad.wartoscPrzed)}</span> →{' '}
                    <span className="font-medium">{zl(podglad.wartoscPo)}</span>
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    {podglad.wgKodu.map((k) => `${k.kod}: ${k.wierszy}`).join(' · ')}
                    {podglad.juzPoprawne > 0 && <> · {t('korekta.juzPoprawne').replace('{n}', String(podglad.juzPoprawne))}</>}
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    {t('korekta.cenaBiezacaTeraz')} {zl(podglad.towar.cenaBiezaca)}
                  </p>
                  <p className="mt-2 flex items-start gap-1.5 text-[var(--destructive)]">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                    <span>{t('korekta.ostrzezenie')}</span>
                  </p>
                </>
              )}
            </div>
          )}

          <ModalError>{blad}</ModalError>
        </FormGrid>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/*
            ⚠️ EKSPORT NIE WYMAGA CENY — to jest osobne, bezpieczne użycie tego okna: „pokaż mi
            w arkuszu wszystkie wydania tego towaru z tego okresu". Wymaga tylko towaru i dat.
          */}
          <Button variant="outline" disabled={!daneOk || eksport.isPending} onClick={() => eksport.mutate()}>
            {eksport.isPending ? <Loader2 className="animate-spin" /> : <Download className="size-4" />}
            {t('korekta.eksport')}
          </Button>
          <Button variant="outline" disabled={!daneOk || !cenaOk || pobierzPodglad.isPending} onClick={() => pobierzPodglad.mutate()}>
            {pobierzPodglad.isPending && <Loader2 className="animate-spin" />}
            {t('korekta.pokaz')}
          </Button>
          {/*
            ⚠️ ZATWIERDZENIE DOSTĘPNE DOPIERO PO PODGLĄDZIE. SPS nie wersjonuje rejestru, więc
            pomyłka w dacie jest nieodwracalna inaczej niż drugą korektą, wykonaną z pamięci.
          */}
          <Button
            className={cn(podglad === null && 'hidden')}
            disabled={podglad === null || podglad.doZmiany === 0 || zatwierdz.isPending}
            onClick={() => zatwierdz.mutate()}
          >
            {zatwierdz.isPending ? <Loader2 className="animate-spin" /> : <Wallet className="size-4" />}
            {t('korekta.zatwierdz')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
